// Frequency Wavelength converter (inverse relationship: wavelength = c / frequency)
// Using hertz as base, with wavelength conversions using speed of light c = 299792458 m/s
export const frequencyWavelengthUnits = [
  { id: 'hertz', name: 'Hertz', symbol: 'Hz', plural: 'Hertz', factor: 1, type: 'frequency' },
  { id: 'exahertz', name: 'Exahertz', symbol: 'EHz', plural: 'Exahertz', factor: 1e18, type: 'frequency' },
  { id: 'petahertz', name: 'Petahertz', symbol: 'PHz', plural: 'Petahertz', factor: 1e15, type: 'frequency' },
  { id: 'terahertz', name: 'Terahertz', symbol: 'THz', plural: 'Terahertz', factor: 1e12, type: 'frequency' },
  { id: 'gigahertz', name: 'Gigahertz', symbol: 'GHz', plural: 'Gigahertz', factor: 1e9, type: 'frequency' },
  { id: 'megahertz', name: 'Megahertz', symbol: 'MHz', plural: 'Megahertz', factor: 1e6, type: 'frequency' },
  { id: 'kilohertz', name: 'Kilohertz', symbol: 'kHz', plural: 'Kilohertz', factor: 1e3, type: 'frequency' },
  // Wavelength units (factor represents wavelength in meters at 1 Hz, converted via c)
  { id: 'meter-wavelength', name: 'Meter', symbol: 'm', plural: 'Meters', factor: 1, type: 'wavelength' },
  { id: 'exameter-wavelength', name: 'Exameter', symbol: 'Em', plural: 'Exameters', factor: 1e18, type: 'wavelength' },
  { id: 'petameter-wavelength', name: 'Petameter', symbol: 'Pm', plural: 'Petameters', factor: 1e15, type: 'wavelength' },
  { id: 'terameter-wavelength', name: 'Terameter', symbol: 'Tm', plural: 'Terameters', factor: 1e12, type: 'wavelength' },
  { id: 'gigameter-wavelength', name: 'Gigameter', symbol: 'Gm', plural: 'Gigameters', factor: 1e9, type: 'wavelength' },
  { id: 'megameter-wavelength', name: 'Megameter', symbol: 'Mm', plural: 'Megameters', factor: 1e6, type: 'wavelength' },
  { id: 'kilometer-wavelength', name: 'Kilometer', symbol: 'km', plural: 'Kilometers', factor: 1e3, type: 'wavelength' },
  { id: 'hectometer-wavelength', name: 'Hectometer', symbol: 'hm', plural: 'Hectometers', factor: 1e2, type: 'wavelength' },
  { id: 'dekameter-wavelength', name: 'Dekameter', symbol: 'dam', plural: 'Dekameters', factor: 10, type: 'wavelength' },
  { id: 'decimeter-wavelength', name: 'Decimeter', symbol: 'dm', plural: 'Decimeters', factor: 0.1, type: 'wavelength' },
  { id: 'centimeter-wavelength', name: 'Centimeter', symbol: 'cm', plural: 'Centimeters', factor: 0.01, type: 'wavelength' },
  { id: 'millimeter-wavelength', name: 'Millimeter', symbol: 'mm', plural: 'Millimeters', factor: 0.001, type: 'wavelength' },
  { id: 'micrometer-wavelength', name: 'Micrometer', symbol: 'µm', plural: 'Micrometers', factor: 1e-6, type: 'wavelength' },
  { id: 'nanometer-wavelength', name: 'Nanometer', symbol: 'nm', plural: 'Nanometers', factor: 1e-9, type: 'wavelength' },
];

export const popularFrequencyWavelengthConversions = [
  { from: 'hertz', to: 'meter-wavelength' },
  { from: 'meter-wavelength', to: 'hertz' },
  { from: 'gigahertz', to: 'centimeter-wavelength' },
  { from: 'megahertz', to: 'meter-wavelength' },
  { from: 'nanometer-wavelength', to: 'terahertz' },
  { from: 'kilohertz', to: 'kilometer-wavelength' },
];

const SPEED_OF_LIGHT = 299792458; // m/s

export const getUnitById = (id) => frequencyWavelengthUnits.find(unit => unit.id === id);

export const convertFrequencyWavelength = (value, fromId, toId) => {
  const fromUnit = getUnitById(fromId);
  const toUnit = getUnitById(toId);
  if (!fromUnit || !toUnit) return null;

  // Convert to base (hertz for frequency, meters for wavelength)
  let freqHz;

  if (fromUnit.type === 'frequency') {
    freqHz = value * fromUnit.factor;
  } else {
    // Wavelength to frequency: f = c / λ
    const wavelengthMeters = value * fromUnit.factor;
    freqHz = SPEED_OF_LIGHT / wavelengthMeters;
  }

  // Convert from Hz to target
  if (toUnit.type === 'frequency') {
    return freqHz / toUnit.factor;
  } else {
    // Frequency to wavelength: λ = c / f
    const wavelengthMeters = SPEED_OF_LIGHT / freqHz;
    return wavelengthMeters / toUnit.factor;
  }
};

export const formatConversionTitle = (fromUnit, toUnit) => {
  return `Convert ${fromUnit.plural} to ${toUnit.plural}`;
};

export const generateSEODescription = (fromUnit, toUnit) => {
  return `Free online ${fromUnit.name} to ${toUnit.name} converter. Convert between frequency and wavelength units using the speed of light.`;
};
